import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import {
    Directive,
    ElementRef,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
    DOCUMENT,
} from '@angular/core';

type TooltipConnectedPosition = {
    originX: ConnectedPosition['originX'];
    originY: ConnectedPosition['originY'];
    overlayX: ConnectedPosition['overlayX'];
    overlayY: ConnectedPosition['originY'];
    panelClass: ConnectedPosition['panelClass'];
};

type TooltipPositions = {
    [pos: string]: TooltipConnectedPosition;
};

const tooltipPositionMap: TooltipPositions = {
    // center/top
    top: {
        originX: 'center',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        panelClass: 'top',
    },
    // center/bottom
    bottom: {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        panelClass: 'bottom',
    },
    // center/right
    right: {
        originX: 'end',
        originY: 'center',
        overlayX: 'start',
        overlayY: 'center',
        panelClass: 'right',
    },
    // center/left
    left: {
        originX: 'start',
        originY: 'center',
        overlayX: 'end',
        overlayY: 'center',
        panelClass: 'left',
    },
};

export type TooltipPosition = 'top' | 'bottom' | 'right' | 'left';

@Directive({ selector: '[oscTooltip]' })
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {
    /**
     * Show a custom tooltip on mouseenter and on click.
     * Provide a TemplateRef to use as template for the tooltip content.
     */
    @Input('oscTooltip') public tooltipContentRef: TemplateRef<any> | null = null;
    @Input() public tooltipPositions: TooltipPosition[] = ['top', 'bottom', 'right', 'left'];

    private tooltip!: OverlayRef;
    private needsClickClose = false;

    private get isOpen(): boolean {
        return this.tooltip.hasAttached();
    }

    public constructor(
        private overlay: Overlay,
        private elementRef: ElementRef,
        private viewContainer: ViewContainerRef,
        private zone: NgZone,
        @Inject(DOCUMENT) private document: Document
    ) {}

    public onMouseEnter = () => {
        this.toggleOpen(false, true);
    };

    public onMouseLeave = () => {
        this.toggleOpen(false, false);
    };

    public onClickListener = ({ target }: MouseEvent) => target && this.onClick(target);

    public onClick(target: EventTarget) {
        const el = this.elementRef.nativeElement;

        if (this.isOpen && !el.contains(target)) {
            this.toggleOpen(true, false);
        } else if (el.contains(target)) {
            this.toggleOpen(true, true);
        }
    }

    public ngOnInit(): void {
        const positions = this.tooltipPositions.map((pos) => tooltipPositionMap[pos]);
        this.tooltip = this.overlay.create(this.getOverlayConfig(positions));

        // add click listener outside the Angular scope for performance reasons
        // see https://stackoverflow.com/a/39705497
        this.zone.runOutsideAngular(() => {
            this.document.addEventListener('click', this.onClickListener);
            this.elementRef.nativeElement.addEventListener('mouseenter', this.onMouseEnter);
            this.elementRef.nativeElement.addEventListener('mouseleave', this.onMouseLeave);
        });
    }

    public ngOnDestroy(): void {
        this.tooltip.dispose();

        this.zone.runOutsideAngular(() => {
            this.document.removeEventListener('click', this.onClickListener);
            this.elementRef.nativeElement.removeEventListener('mouseenter', this.onMouseEnter);
            this.elementRef.nativeElement.removeEventListener('mouseleave', this.onMouseLeave);
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!this.tooltip) {
            return;
        }

        const tooltipChange = changes.tooltipContentRef;

        if (tooltipChange) {
            if (this.tooltip.hasAttached()) {
                this.tooltip.detach();
            }

            this.updateTooltip(tooltipChange.currentValue);
        }
    }

    private showTooltip() {
        this.updateTooltip(this.tooltipContentRef);
    }

    private hideTooltip() {
        this.tooltip.detach();
        this.needsClickClose = false;
    }

    private toggleOpen(byClick: boolean, open: boolean) {
        if (open && !this.isOpen) {
            this.showTooltip();
        } else if (!open && this.isOpen && this.needsClickClose === byClick) {
            this.hideTooltip();

            return;
        }
        this.needsClickClose = this.needsClickClose || byClick;
    }

    private updateTooltip(tooltipTemplate: TemplateRef<any> | null): void {
        if (!tooltipTemplate) {
            return;
        }

        /**
         * OverlayRef.attach is using ngZone.onStable observable to get feedback, when the zone is stable and update the
         * position of the attached template accordingly.
         * Because we are running the event listeners outside of the ngZone for performance optimizations, we have
         * to run attach explicitly in the ngZone. Otherwise the onStable observable is not emitting.
         *
         * this._ngZone.onStable.pipe((0,
         *       z.q)(1)).subscribe(()=>{
         *           this.hasAttached() && this.updatePosition()
         *       }
         * )
         */
        this.zone.run(() => this.tooltip.attach(new TemplatePortal(tooltipTemplate, this.viewContainer)));
    }

    private getOverlayConfig(connectedPositions: TooltipConnectedPosition[]): OverlayConfig {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elementRef)
            .withPositions(connectedPositions)
            .withPush(false);
        const scrollStrategy = this.overlay.scrollStrategies.close();

        return {
            positionStrategy,
            scrollStrategy,
            maxWidth: 244,
        };
    }
}
